// Follow scheduled audio times with a shared visual frame for all voices.
// currentTime runs ahead of the speakers. Follow the output-device clock when
// available, rather than applying a fixed visual lead to the rendering clock.
export const DEFAULT_AUDIO_FEEDBACK_OFFSET = -22;
let visualOffset = DEFAULT_AUDIO_FEEDBACK_OFFSET / 1000;

// Local listening calibration only: negative values show highlights earlier.
export function setAudioFeedbackOffset(milliseconds) {
    visualOffset = Number.isFinite(milliseconds) ? Math.max(-200, Math.min(200, milliseconds)) / 1000 : 0;
}

export function createAudioFeedback(context) {
    let pending = [];
    let frame;

    function wake() {
        if (!frame && pending.length && context.state === 'running') frame = requestAnimationFrame(update);
    }

    function update() {
        frame = undefined;
        if (context.state !== 'running') return;
        const output = context.getOutputTimestamp?.();
        const elapsed = output ? (performance.now() - output.performanceTime) / 1000 : 0;
        // Startup/resume can briefly report zero or stale output timestamps.
        // The fallback uses reported latency; neither path delays the audio.
        const now = output?.contextTime > 0 && output.performanceTime > 0 && elapsed < 0.25
            ? Math.min(context.currentTime, output.contextTime + elapsed)
            : context.currentTime - (context.baseLatency || 0) - (context.outputLatency || 0);
        const visualTime = now - visualOffset;
        const due = pending.filter(item => item.time <= visualTime);
        pending = pending.filter(item => item.time > visualTime);
        for (const {time, callback} of due) {
            // Never replay a backlog of flashes after a background tab or stall.
            if (!document.hidden && visualTime - time < 0.2) callback();
        }
        wake();
    }

    context.addEventListener('statechange', wake);
    window.addEventListener('pagehide', () => {
        pending = [];
        cancelAnimationFrame(frame);
        frame = undefined;
    });
    return (time, callback) => {
        const item = {time, callback};
        pending.push(item);
        wake();
        return () => {
            pending = pending.filter(entry => entry !== item);
            if (!pending.length) {
                cancelAnimationFrame(frame);
                frame = undefined;
            }
        };
    };
}
